import {PaymentMethod} from '@sollapay/enums';
import {
  Divider,
  ListFilterCheckboxSection,
  ListFilterCreatedAtSection,
  ListFilterHeader
} from '@sollapay/ui/components';
import {parseMoney} from '@sollapay/utils';
import {useTranslation} from 'react-i18next';

import {
  CREATED_AT_FILTER_VALUES,
  DEFAULT_INSTRUCTIONS_FILTER_STATE,
  UNASSIGNED_BUYER_FILTER_VALUE
} from './config/instructions-filter.config';
import {isInstructionsFilterActive} from './utils/instructions-filter.utils';

import type {InstructionsFilterState} from './config/instructions-filter.config';
import type {CreatedAtFilter} from '@sollapay/ui/components';
import type {FC} from 'react';

export interface InstructionsFilterMenuProps {
  filter: InstructionsFilterState;
  onChange: (filter: InstructionsFilterState) => void;
  purchaseOptions: {id: string; label: string}[];
  buyerOptions: {id: string; label: string}[];
}

const CREATED_AT_KEYS: Record<CreatedAtFilter, string> = {
  all: 'filter.createdAt.all',
  this_week: 'filter.createdAt.thisWeek',
  '7d': 'filter.createdAt.last7days',
  '30d': 'filter.createdAt.last30days',
  '90d': 'filter.createdAt.last90days'
};

export const InstructionsFilterMenu: FC<InstructionsFilterMenuProps> = ({
  filter,
  onChange,
  purchaseOptions,
  buyerOptions
}) => {
  const {t} = useTranslation('trustAccounts');

  const createdAtOptions = CREATED_AT_FILTER_VALUES.map(value => ({
    value,
    label: t(CREATED_AT_KEYS[value])
  }));

  const paymentMethodOptions = Object.values(PaymentMethod).map(method => ({
    value: method,
    label: t(`view.buyers.paymentRequest.methods.${method}`)
  }));

  const assetOptions = purchaseOptions.map(({id, label}) => ({value: id, label}));
  const buyerFilterOptions = [
    ...buyerOptions.map(({id, label}) => ({value: id, label})),
    {value: UNASSIGNED_BUYER_FILTER_VALUE, label: t('view.instructions.filter.unassignedBuyer')}
  ];

  return (
    <div data-slot="instructions-filter-menu" className="flex flex-col py-2">
      <ListFilterHeader
        title={t('view.instructions.filter.title')}
        clearLabel={t('view.instructions.filter.clearAll')}
        isActive={isInstructionsFilterActive(filter)}
        onClearAll={() => onChange(DEFAULT_INSTRUCTIONS_FILTER_STATE)}
      />
      {assetOptions.length > 0 && (
        <>
          <ListFilterCheckboxSection
            title={t('view.instructions.filter.sections.asset')}
            options={assetOptions}
            selected={filter.purchaseIds}
            onChange={vals => onChange({...filter, purchaseIds: vals})}
          />
          <Divider className="my-1" />
        </>
      )}
      <ListFilterCheckboxSection
        title={t('view.instructions.filter.sections.buyer')}
        options={buyerFilterOptions}
        selected={filter.buyerPartyIds}
        onChange={vals => onChange({...filter, buyerPartyIds: vals})}
      />
      <Divider className="my-1" />
      <ListFilterCheckboxSection
        title={t('view.instructions.filter.sections.paymentMethod')}
        options={paymentMethodOptions}
        selected={filter.paymentMethods}
        onChange={vals => onChange({...filter, paymentMethods: vals as PaymentMethod[]})}
      />
      <Divider className="my-1" />
      <ListFilterCreatedAtSection
        sectionLabel={t('filter.sections.createdAt')}
        options={createdAtOptions}
        createdAt={filter.datePreset}
        hasDateRange={false}
        onChange={datePreset => onChange({...filter, datePreset})}
      />
      <Divider className="my-1" />
      <div className="px-3 pb-1 pt-2">
        <span className="text-xs font-semibold text-fg-tertiary">
          {t('view.instructions.filter.sections.amountRange')}
        </span>
      </div>
      <div className="flex items-start gap-2 px-3 pb-2 pt-1">
        <div className="flex flex-1 flex-col gap-1.5">
          <span className="text-xs font-medium text-fg-secondary">
            {t('view.instructions.filter.amountRange.from')}
          </span>
          <div className="relative">
            <span
              data-slot="icon"
              className="pointer-events-none absolute inset-e-2 top-1/2 -translate-y-1/2 text-xs text-fg-tertiary"
            >
              ₪
            </span>
            <input
              type="number"
              min={0}
              aria-label={t('view.instructions.filter.amountRange.from')}
              value={filter.amountMin ?? ''}
              onChange={e => {
                const val =
                  e.target.value === '' ? undefined : parseMoney(e.target.value).toNumber();
                onChange({...filter, amountMin: val});
              }}
              className="input-base h-9 w-full pe-6 text-sm"
              placeholder="0"
            />
          </div>
        </div>
        <div className="flex flex-1 flex-col gap-1.5">
          <span className="text-xs font-medium text-fg-secondary">
            {t('view.instructions.filter.amountRange.to')}
          </span>
          <div className="relative">
            <span
              data-slot="icon"
              className="pointer-events-none absolute inset-e-2 top-1/2 -translate-y-1/2 text-xs text-fg-tertiary"
            >
              ₪
            </span>
            <input
              type="number"
              min={0}
              aria-label={t('view.instructions.filter.amountRange.to')}
              value={filter.amountMax ?? ''}
              onChange={e => {
                const val =
                  e.target.value === '' ? undefined : parseMoney(e.target.value).toNumber();
                onChange({...filter, amountMax: val});
              }}
              className="input-base h-9 w-full pe-6 text-sm"
              placeholder="∞"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
